import { useEffect, useRef, useState, useCallback } from 'react';
import { isTokenValid } from '../utils/auth';

const useSessionTimeout = ({
  timeoutMinutes = 20,
  warningMinutes = 2,
  onTimeout,
  onTokenRefresh,
  enabled = true,
  tokenKey = 'access_token',
} = {}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [warningReason, setWarningReason] = useState('inactivity');

  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);
  const countdownRef = useRef(null);
  const hasLoggedOutRef = useRef(false);
  // Ref para evitar problemas de closure con showWarning
  const showWarningRef = useRef(false);
  const isRefreshingRef = useRef(false);

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;

  const getTokenRemainingMs = useCallback(() => {
    try {
      const token = sessionStorage.getItem(tokenKey);
      if (!token) return 0;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const expMs = payload.exp * 1000;
      return Math.max(0, expMs - Date.now());
    } catch (e) {
      return 0;
    }
  }, [tokenKey]);

  const registerActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const extendSession = useCallback(async () => {
    // Si es por token, intentar refrescar
    if (warningReason === 'token' && onTokenRefresh) {
      try {
        await onTokenRefresh();
      } catch (error) {
        logout();
        return;
      }
    }

    // Limpiar el countdown primero
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    // Resetear el tiempo de última actividad
    lastActivityRef.current = Date.now();

    // Actualizar el ref antes del estado para evitar race conditions
    showWarningRef.current = false;

    // Resetear estados
    setShowWarning(false);
    setRemainingSeconds(0);
    setWarningReason('inactivity');
  }, [warningReason, onTokenRefresh]);

  const logout = useCallback(() => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    // Limpiar todos los intervalos
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    showWarningRef.current = false;
    setShowWarning(false);

    if (onTimeout) {
      onTimeout();
    }
  }, [onTimeout]);

  // Efecto principal para verificar estado de sesión
  useEffect(() => {
    if (!enabled) {
      return;
    }

    hasLoggedOutRef.current = false;
    showWarningRef.current = false;
    lastActivityRef.current = Date.now();

    const startCountdown = (initialSeconds, reason) => {
      // Limpiar countdown anterior si existe
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }

      setWarningReason(reason);
      showWarningRef.current = true;
      setShowWarning(true);
      setRemainingSeconds(initialSeconds);

      countdownRef.current = setInterval(() => {
        // Verificar si el usuario extendió la sesión
        if (!showWarningRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          return;
        }

        if (!isTokenValid(tokenKey)) {
          logout();
          return;
        }

        const now = Date.now();
        const inactivityRemaining = timeoutMs - (now - lastActivityRef.current);
        const tokenRemaining = getTokenRemainingMs();
        const effectiveRemaining = Math.min(inactivityRemaining, tokenRemaining);
        const currentReason = tokenRemaining < inactivityRemaining ? 'token' : 'inactivity';

        if (effectiveRemaining <= 0) {
          logout();
        } else {
          setRemainingSeconds(Math.ceil(effectiveRemaining / 1000));
          setWarningReason(currentReason);
        }
      }, 1000);
    };

    const checkStatus = () => {
      // Si ya estamos mostrando el warning, no volver a verificar
      if (showWarningRef.current) {
        return;
      }

      if (!isTokenValid(tokenKey)) {
        logout();
        return;
      }

      const now = Date.now();
      const inactivityElapsed = now - lastActivityRef.current;
      const inactivityRemaining = timeoutMs - inactivityElapsed;
      const tokenRemaining = getTokenRemainingMs();
      const effectiveRemaining = Math.min(inactivityRemaining, tokenRemaining);
      const reason = tokenRemaining < inactivityRemaining ? 'token' : 'inactivity';

      if (effectiveRemaining <= 0) {
        logout();
        return;
      }

      // Auto-refresh preventivo: si el token está por vencer pero el usuario está activo
      if (
        tokenRemaining <= 5 * 60 * 1000 &&
        tokenRemaining > warningMs &&
        inactivityRemaining > warningMs &&
        onTokenRefresh &&
        !isRefreshingRef.current
      ) {
        isRefreshingRef.current = true;
        onTokenRefresh()
          .catch(() => {})
          .finally(() => { isRefreshingRef.current = false; });
      }

      // Mostrar warning si estamos dentro del período de advertencia
      if (effectiveRemaining <= warningMs) {
        startCountdown(Math.ceil(effectiveRemaining / 1000), reason);
      }
    };

    // Verificar inmediatamente
    checkStatus();

    // Verificar periódicamente
    checkIntervalRef.current = setInterval(checkStatus, 5000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [enabled, timeoutMs, warningMs, logout, getTokenRemainingMs, onTokenRefresh]);

  // Efecto para registrar actividad del usuario
  useEffect(() => {
    if (!enabled) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      window.addEventListener(event, registerActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, registerActivity);
      });
    };
  }, [enabled, registerActivity]);

  // Efecto para manejar cambios de visibilidad de la página
  useEffect(() => {
    if (!enabled) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (!isTokenValid(tokenKey)) {
          logout();
          return;
        }

        // Solo verificar timeout por inactividad si no estamos mostrando warning
        if (!showWarningRef.current) {
          const inactivityElapsed = Date.now() - lastActivityRef.current;
          if (inactivityElapsed >= timeoutMs) {
            logout();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, timeoutMs, logout]);

  const formatTime = () => {
    const mins = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  return {
    showWarning,
    remainingSeconds,
    remainingFormatted: formatTime(),
    extendSession,
    logout,
    warningReason,
  };
};

export default useSessionTimeout;
